// HAWELO — cart state (persisted via Shopify Cart ID in localStorage)
import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  storefront,
  Q_CART,
  M_CART_CREATE,
  M_CART_LINES_ADD,
  M_CART_LINES_UPDATE,
  M_CART_LINES_REMOVE,
  normaliseCart,
} from './shopify';

const STORAGE_KEY = 'hawelo_cart_id';
const CartCtx = createContext(null);

export function CartProvider({ children }) {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadExisting = useCallback(async () => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return;
    try {
      const data = await storefront(Q_CART, { id: saved });
      if (data?.cart) setCart(normaliseCart(data.cart));
      else localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  useEffect(() => { loadExisting(); }, [loadExisting]);

  const addLine = useCallback(async (variantId, quantity = 1) => {
    setLoading(true);
    try {
      const lines = [{ merchandiseId: variantId, quantity }];
      let next;
      if (!cart?.id) {
        const data = await storefront(M_CART_CREATE, { lines });
        next = data.cartCreate.cart;
        if (next?.id) localStorage.setItem(STORAGE_KEY, next.id);
      } else {
        const data = await storefront(M_CART_LINES_ADD, { cartId: cart.id, lines });
        next = data.cartLinesAdd.cart;
      }
      setCart(normaliseCart(next));
    } finally {
      setLoading(false);
    }
  }, [cart?.id]);

  const updateLine = useCallback(async (lineId, quantity) => {
    if (!cart?.id) return;
    setLoading(true);
    try {
      const data = await storefront(M_CART_LINES_UPDATE, {
        cartId: cart.id,
        lines: [{ id: lineId, quantity }],
      });
      setCart(normaliseCart(data.cartLinesUpdate.cart));
    } finally {
      setLoading(false);
    }
  }, [cart?.id]);

  const removeLine = useCallback(async (lineId) => {
    if (!cart?.id) return;
    setLoading(true);
    try {
      const data = await storefront(M_CART_LINES_REMOVE, {
        cartId: cart.id,
        lineIds: [lineId],
      });
      setCart(normaliseCart(data.cartLinesRemove.cart));
    } finally {
      setLoading(false);
    }
  }, [cart?.id]);

  const count = cart?.totalQuantity || 0;

  return (
    <CartCtx.Provider value={{ cart, loading, count, addLine, updateLine, removeLine }}>
      {children}
    </CartCtx.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error('useCart must be inside CartProvider');
  return ctx;
}
